import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import intersectionWith from 'lodash/intersectionWith';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { ratingApi } from 'apis/rating';
import {
  getCSATLanguages,
  SupportedCSATLanguage,
  SupportedCSATLanguageCode,
} from 'app/views/layout/views/settings/views/satisfaction/utils/supportedCSATLanguages/supportedCSATLanguages';
import { satisfactionActions } from 'components/conversation/actions/satisfaction';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { ConversationHeaderCsatDropdown } from './components/csatDropdown/csatDropdown';
import { LanguageCodeItem } from './types';
import {
  getTooltipContent,
  inferSurveyOfferStateFromConversationMessages,
} from './utils';

const defaultLanguage = 'en';
const supportedLanguages = getCSATLanguages();

export const ConversationHeaderCsatMenu = (): JSX.Element | null => {
  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const agentHasCsatPermission = useHasAccess('conversation.satisfaction');
  const agentHasReplyPermission = useHasAccess('conversation.reply');
  const [{ messages }, { id: csid }, asyncStatus] = useAppSelector(
    (state) => [
      state.conversation.metaConversation,
      state.conversation.metaConversation.conversation,
      state.conversation.asyncStatus,
    ],
    shallowEqual,
  );

  const inferredSurveyOfferState =
    inferSurveyOfferStateFromConversationMessages(messages);

  const {
    data: ratingInfoData,
    isLoading: isLoadingFutureRatingInfo,
    refetch: refetchRatingInfo,
  } = ratingApi.useGetFutureRatingInfoQuery(
    csid ? { csid: csid.toString() } : skipToken,
  );

  const isCsatEligible = useMemo(
    () => ratingInfoData?.data?.csat_eligible ?? false,
    [ratingInfoData?.data?.csat_eligible],
  );

  const isUserExcluded = useMemo(
    () => ratingInfoData?.data?.user_excluded_from_csat ?? false,
    [ratingInfoData?.data?.user_excluded_from_csat],
  );

  const isCancellable = useMemo(
    () => ratingInfoData?.data?.cancellable ?? true,
    [ratingInfoData?.data?.cancellable],
  );

  const availableSurveyLanguages = useMemo(
    () => ratingInfoData?.data?.survey_languages ?? [],
    [ratingInfoData?.data?.survey_languages],
  );

  /**
   * The languages that the agent can select from. This is coming from the api
   * call to rating api. The list is enriched with the label and description
   * using the hardcoded list of supported languages.
   */
  const selectableLanguages: LanguageCodeItem[] = useMemo(() => {
    return intersectionWith(
      supportedLanguages,
      availableSurveyLanguages,
      ({ code }, langCode) => code === langCode,
    ).map(({ code, label }: SupportedCSATLanguage) => ({
      id: code,
      name: label,
      description: code,
    }));
  }, [availableSurveyLanguages]);

  const isLoading =
    asyncStatus.getConversation === 'loading' ||
    asyncStatus.claim === 'loading' ||
    asyncStatus.close === 'loading' ||
    isLoadingFutureRatingInfo;

  const shouldHideButton =
    !isCsatEligible ||
    !isCancellable ||
    isUserExcluded ||
    !agentHasReplyPermission;

  const [{ id: conversationId, attributes: conversationAttributes }] =
    useAppSelector((state) => [
      state.conversation.metaConversation.conversation,
    ]);

  const surveyLanguage: SupportedCSATLanguageCode | undefined = useMemo(() => {
    // If the conversationlanguage is in the available languages, return it
    if (
      conversationAttributes?.language &&
      availableSurveyLanguages.includes(conversationAttributes.language)
    ) {
      return conversationAttributes.language as SupportedCSATLanguageCode;
    }
    // If the default language is in the available languages, return it
    if (availableSurveyLanguages.includes(defaultLanguage)) {
      return defaultLanguage;
    }
    // If no language is available, return undefined. No language is considered selected.
    return undefined;
  }, [availableSurveyLanguages, conversationAttributes?.language]);

  const handleLanguageSelect = ({ id: languageCode }: LanguageCodeItem) => {
    if (!agentHasReplyPermission) {
      notify(
        {
          title: 'Failed to change survey language',
          text: 'You do not have permission to change the survey language',
        },
        NotificationType.Error,
      );

      return;
    }

    ampli.conversationCsatMenuLanguageSelected({
      languageCode,
    });
    bindActionToPromise(
      dispatch,
      satisfactionActions.changeSatisfactionLanguageRequest,
    )({ csid: conversationId, language: languageCode });
  };

  const handleCancelCsat = () => {
    if (!agentHasCsatPermission) {
      notify(
        {
          title: 'Failed to cancel survey',
          text: 'You do not have permission to cancel the survey',
        },
        NotificationType.Error,
      );

      return;
    }

    ampli.conversationCsatMenuCancelOptionClicked();
    bindActionToPromise(
      dispatch,
      satisfactionActions.cancelSatisfactionRequest,
    )({ csid: conversationId });
  };

  // Determine button display text
  const getButtonDisplayText = () => {
    if (inferredSurveyOfferState) {
      // Survey offering has been triggered and has come to a conclusion
      return inferredSurveyOfferState;
    }

    // Survey is not yet triggered (no offer state) so the current language is shown
    if (surveyLanguage) {
      return surveyLanguage;
    }

    // No language selected so the agent with right permissions can choose a language
    if (agentHasReplyPermission) {
      return 'No match';
    }

    return 'Not available';
  };

  const tooltipContent = getTooltipContent({
    isUserExcluded,
    isCancellable,
    hasNoLanguage: !surveyLanguage,
    canChangeLanguage: agentHasReplyPermission,
  });

  if (shouldHideButton) {
    return null;
  }

  return (
    <ConversationHeaderCsatDropdown
      isLoading={isLoading}
      tooltipContent={tooltipContent}
      onRefreshRatingInfo={refetchRatingInfo}
      surveyLanguage={surveyLanguage}
      availableLanguageCodeItems={selectableLanguages}
      handleLanguageSelect={handleLanguageSelect}
      handleCancelCsat={agentHasCsatPermission ? handleCancelCsat : undefined}
      buttonDisplayText={getButtonDisplayText()}
    />
  );
};
